import { supabase } from '@/lib/supabase/client';
import * as FileSystem from 'expo-file-system/legacy';
import * as MediaLibrary from 'expo-media-library';
import { decode } from 'base64-arraybuffer';
import { FileUploadResult } from '../types';
import { Platform, Alert } from 'react-native';

/**
 * Upload file to both chat-files and family-gallery buckets
 */
export const uploadChatFile = async (
  fileUri: string,
  chatId: string,
  userId: string,
  fileType: string
): Promise<FileUploadResult> => {
  try {
    // Get file info
    const fileInfo = await FileSystem.getInfoAsync(fileUri);
    if (!fileInfo.exists) {
      throw new Error('File does not exist');
    }

    // Generate unique filename
    const timestamp = Date.now();
    const extension = fileUri.split('.').pop() || 'file';
    const fileName = `${userId}_${timestamp}.${extension}`;

    // Read file as base64
    const base64 = await FileSystem.readAsStringAsync(fileUri, {
      encoding: FileSystem.EncodingType.Base64,
    });

    // Convert base64 to ArrayBuffer using base64-arraybuffer library
    const arrayBuffer = decode(base64);

    // 1. Upload to chat-files bucket (chat-specific)
    const chatPath = `chats/${chatId}/${fileName}`;
    const { data: chatData, error: chatError } = await supabase.storage
      .from('chat-files')
      .upload(chatPath, arrayBuffer, {
        contentType: fileType,
        cacheControl: '3600',
        upsert: false,
      });

    if (chatError) throw chatError;

    // 2. Upload to family-gallery bucket (permanent storage)
    const galleryPath = `users/${userId}/chat-uploads/${fileName}`;
    const { data: galleryData, error: galleryError } = await supabase.storage
      .from('family-gallery')
      .upload(galleryPath, arrayBuffer, {
        contentType: fileType,
        cacheControl: '3600',
        upsert: false,
      });

    if (galleryError) {
      console.warn('Failed to upload to gallery:', galleryError);
      // Don't throw - gallery is optional
    }

    // Get public URLs
    const { data: chatUrlData } = supabase.storage
      .from('chat-files')
      .getPublicUrl(chatPath);

    const { data: galleryUrlData } = supabase.storage
      .from('family-gallery')
      .getPublicUrl(galleryPath);

    return {
      chatFileUrl: chatUrlData.publicUrl,
      galleryFileUrl: galleryUrlData.publicUrl,
      fileName,
      fileType,
      fileSize: fileInfo.size || 0,
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Delete file from chat-files bucket
 * Note: Does not delete from family-gallery (permanent)
 */
export const deleteChatFile = async (
  chatId: string,
  fileName: string
): Promise<void> => {
  try {
    const path = `chats/${chatId}/${fileName}`;
    const { error } = await supabase.storage
      .from('chat-files')
      .remove([path]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Get file type from URI
 */
export const getFileTypeFromUri = (uri: string): string => {
  const extension = uri.split('.').pop()?.toLowerCase() || '';
  
  // Images
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(extension)) {
    return `image/${extension === 'jpg' ? 'jpeg' : extension}`;
  }
  
  // Videos
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
    return `video/${extension}`;
  }
  
  // Audio
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(extension)) {
    return `audio/${extension}`;
  }
  
  // Documents
  if (extension === 'pdf') return 'application/pdf';
  if (['doc', 'docx'].includes(extension)) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  if (['xls', 'xlsx'].includes(extension)) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (['ppt', 'pptx'].includes(extension)) {
    return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  }
  
  return 'application/octet-stream';
};

/**
 * Determine message type from file type
 */
export const getMessageTypeFromFileType = (fileType: string): 'image' | 'video' | 'audio' | 'file' => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType.startsWith('audio/')) return 'audio';
  return 'file';
};

/**
 * Download file to device
 */
export const downloadFile = async (
  fileUrl: string,
  fileName: string,
  fileType: string
): Promise<boolean> => {
  try {
    // Only request permissions for images and videos (not audio)
    const isMediaFile = fileType.startsWith('image/') || fileType.startsWith('video/');

    if (isMediaFile) {
      // Request write-only permission for saving to gallery
      const { status } = await MediaLibrary.requestPermissionsAsync(false);
      if (status !== 'granted') {
        Alert.alert(
          'Toestemming vereist',
          'We hebben toestemming nodig om bestanden op te slaan in je galerij.',
          [{ text: 'OK' }]
        );
        return false;
      }
    }

    // Create download path
    const fileUri = FileSystem.documentDirectory + fileName;

    // Download file
    const downloadResult = await FileSystem.downloadAsync(fileUrl, fileUri);

    if (downloadResult.status !== 200) {
      throw new Error('Download failed');
    }

    // Save to media library for images and videos
    if (isMediaFile) {
      const asset = await MediaLibrary.createAssetAsync(downloadResult.uri);
      await MediaLibrary.createAlbumAsync('Staatsen Familie', asset, false);

      Alert.alert('Succes!', 'Het bestand is opgeslagen in je galerij.', [{ text: 'OK' }]);
    } else {
      // For other file types, just keep in app directory
      Alert.alert('Succes!', 'Het bestand is gedownload.', [{ text: 'OK' }]);
    }

    return true;
  } catch (error) {
    console.error('Download error:', error);
    Alert.alert('Fout', 'Er is iets misgegaan bij het downloaden van het bestand.', [
      { text: 'OK' },
    ]);
    return false;
  }
};

/**
 * Share file functionality
 */
export const shareFile = async (
  fileUrl: string,
  fileName: string
): Promise<void> => {
  try {
    // For now, just download the file
    // In future, can use expo-sharing for proper sharing
    const fileUri = FileSystem.documentDirectory + fileName;
    await FileSystem.downloadAsync(fileUrl, fileUri);
    
    Alert.alert('Info', 'Bestand gedownload. Delen komt binnenkort beschikbaar!');
  } catch (error) {
    console.error('Error sharing file:', error);
    throw error;
  }
};
